import { StyleSheet, View } from 'react-native'
import { Text, useTheme } from 'react-native-paper'
import { useSemanticColors } from '@/constants/SemanticColors'
import type { LocationAuthSnapshot } from '@/hooks/monitor/useUsersLocationAuth'

interface Props {
    auth: LocationAuthSnapshot | undefined
}

interface Variant {
    color: string
    label: string
}

function variantFor(
    auth: LocationAuthSnapshot | undefined,
    semantic: ReturnType<typeof useSemanticColors>,
    outline: string
): Variant | null {
    if (!auth) return null
    switch (auth.status) {
        case 'always':
            return auth.accuracyAuthorization === 'reduced'
                ? { color: semantic.warning, label: 'Always · Reduced' }
                : { color: semantic.success, label: 'Always' }
        case 'whenInUse':
            return { color: semantic.warning, label: 'When In Use' }
        case 'denied':
            return { color: semantic.error, label: 'Denied' }
        case 'restricted':
            return { color: semantic.error, label: 'Restricted' }
        case 'notDetermined':
            return { color: outline, label: 'Not asked' }
        default:
            return { color: outline, label: 'Unknown' }
    }
}

export function LocationAuthIndicator({ auth }: Props) {
    const theme = useTheme()
    const semantic = useSemanticColors()
    const variant = variantFor(auth, semantic, theme.colors.outline)
    if (!variant) return null

    return (
        <View style={styles.container}>
            <View style={[styles.dot, { backgroundColor: variant.color }]} />
            <Text variant="labelSmall" style={[styles.label, { color: semantic.mutedText }]}>
                {variant.label}
            </Text>
        </View>
    )
}

const styles = StyleSheet.create({
    container: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
    },
    dot: {
        width: 8,
        height: 8,
        borderRadius: 4,
    },
    label: {
        fontWeight: '500',
    },
})
